import Typography from '@mui/material/Typography';
import { useUpdateGoalSortMode, useUpdateUserSettings } from 'features/user';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowUp } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import { useUser } from 'shared/hooks/use-user';
import { GoalSortingMode, OrderDirection } from 'shared/types/sorting-mode';

import * as Styled from './sort-bar.style';

const sortOptions: { mode: GoalSortingMode; label: string }[] = [
  {
    mode: GoalSortingMode.Name,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Name}`,
  },
  {
    mode: GoalSortingMode.Progress,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Progress}`,
  },
  {
    mode: GoalSortingMode.Impact,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Impact}`,
  },
  {
    mode: GoalSortingMode.StartDate,
    label: `general.labels.goals.sort.options.${GoalSortingMode.StartDate}`,
  },
  {
    mode: GoalSortingMode.Deadline,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Deadline}`,
  },
  {
    mode: GoalSortingMode.Tasks,
    label: `general.labels.goals.sort.options.${GoalSortingMode.Tasks}`,
  },
];

export const SortBar: React.FC = () => {
  const { t } = useTranslation();
  const user = useUser();
  const setSortMode = useUpdateGoalSortMode();
  const updateUserSettings = useUpdateUserSettings();

  const currentSort = user?.settings?.goalSortingMode;
  const direction = user?.settings?.goalSortDirection ?? OrderDirection.Asc;

  const handleClick = useCallback(
    (mode: GoalSortingMode) => {
      if (currentSort === mode) {
        updateUserSettings({
          goalSortDirection:
            direction === OrderDirection.Asc
              ? OrderDirection.Desc
              : OrderDirection.Asc,
        });
      } else {
        setSortMode(mode);
        updateUserSettings({ goalSortDirection: OrderDirection.Asc });
      }
    },
    [currentSort, direction, setSortMode, updateUserSettings],
  );

  return (
    <Styled.Container>
      {sortOptions.map(({ mode, label }) => {
        const isActive = currentSort === mode;

        return (
          <Styled.Item key={mode} $mode={mode}>
            <Styled.Button $active={isActive} onClick={() => handleClick(mode)}>
              <Typography variant="buttonSmall" component="span">
                {t(label)}
              </Typography>
              {isActive && (
                <Styled.DirectionIcon $desc={direction === OrderDirection.Desc}>
                  <Icon icon={ArrowUp} />
                </Styled.DirectionIcon>
              )}
            </Styled.Button>
          </Styled.Item>
        );
      })}
    </Styled.Container>
  );
};
